<template>
    <base-demo-flow
        :phone_key="phone_key"
        :phone_mode="phone_mode"
        :browser_content_key="current_step"
        business_name="Caring Hands Home Care"
        phone_number="(315) 555-0147"
        browser_url="msgs.caringhands.com/shifts/1jk23lm"
        phone_label="Jamie's Phone"
        :current_step_number="current_step_number"
        :total_steps="total_steps"
        :progress_percentage="progress_percentage"
        :current_step_name="current_step_name"
        :can_go_previous="can_go_previous"
        :can_go_next="can_go_next"
        :steps="formatted_steps"
        :include_backdrop="false"
        @restart="resetDemo"
        @previous="goToPreviousStep"
        @next="goToNextStep"
        @step-click="goToStep"
    >
        <template #explainer>
            <component
                :is="explainer_component"
                :shift_data="shift_data"
                :shift_status="shift_status"
                @reset="resetDemo"
                @reset-web-experience="resetWebExperience"
                @simulate-other-claim="handleOtherCaregiverClaim"
                @undo-simulate-other-claim="handleUndoOtherCaregiverClaim"
            />
        </template>

        <template #phone-messages>
            <mock-text-conversation
                :messages="conversation"
                :previously_shown_count="previously_shown_message_count"
            />
        </template>

        <template #phone-browser>
            <mock-shift-landing
                v-if="current_step === 'landing-page'"
                :shift_data="shift_data"
                :shift_status="shift_status"
                @claim="handleClaimShift"
                @pass="handlePassShift"
                @reset-web-experience="resetWebExperience"
            />
        </template>
    </base-demo-flow>
</template>

<script>
import BaseDemoFlow from '@/components/demos/shared/base-demo-flow.vue';
import DemoStepManager from '@/mixins/demo-step-manager.js';
import NoIndexPage from '@/mixins/noindex-page.js';
import ExplainerInitialMessage from '@/components/demos/healthcare-shift/explainer-initial-message.vue';
import ExplainerLandingPage from '@/components/demos/healthcare-shift/explainer-landing-page.vue';
import MockTextConversation from '@/components/demos/shared/mock-text-conversation.vue';
import MockShiftLanding from '@/components/demos/healthcare-shift/mock-shift-landing.vue';

export default {
    name: 'DemoHealthcareShiftFlow',

    mixins: [DemoStepManager, NoIndexPage],

    components: {
        BaseDemoFlow,
        ExplainerInitialMessage,
        ExplainerLandingPage,
        MockTextConversation,
        MockShiftLanding
    },

    data() {
        return {
            shift_status: 'available', // 'available', 'claimed', 'claimed_by_other', 'passed'
            shift_data: {
                patient_name: 'Maria S.',
                patient_age: 82,
                patient_gender: 'female',
                location: 'Canton, NY',
                date: this.getTomorrowFormatted(),
                start_time: '4:00 PM',
                end_time: '8:00 PM',
                mobility: 'Mobile with walker',
                cognitive_concerns: 'Sundowning',
                adl_support: ['Dinner', 'Bathing'],
                medication_schedule: 'None'
            },
            caretaker_data: {
                name: 'Jamie L.',
            },
        }
    },

    computed: {
        explainer_component() {
            switch (this.current_step) {
                case 'initial-text':
                    return 'ExplainerInitialMessage';
                case 'landing-page':
                    return 'ExplainerLandingPage';
                default:
                    return null;
            }
        },

        initial_message_text() {
            return `New Shift Available: ${this.shift_data.location}\n\nWe are looking for coverage for a ${this.shift_data.patient_gender} client tomorrow, ${this.shift_data.date}, from ${this.shift_data.start_time} - ${this.shift_data.end_time}.\n\nView shift details and claim here:\n\n{link}\n\nThanks!\n\nReply STOP to opt out.`;
        },

        short_link() {
            return {
                url: '#',
                text: 'smsly.io/1jk23lm',
                preventDefault: true,
                callback: this.handleGoToLandingPage
            };
        },

        conversation() {
            return [
                {
                    type: 'customer',
                    text: this.initial_message_text,
                    link: this.short_link,
                    time: '2:30 PM'
                }
            ];
        }
    },

    methods: {
        getTomorrowFormatted() {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const options = { weekday: 'long', month: 'long', day: 'numeric' };
            return tomorrow.toLocaleDateString('en-US', options);
        },

        getStepConfig() {
            return {
                initial_step: 'initial-text',
                initial_phone_mode: 'messages',
                steps: ['initial-text', 'landing-page'],
                step_names: {
                    'initial-text': 'SMS Notification',
                    'landing-page': 'Shift Details'
                },
                step_definitions: {
                    'initial-text': {
                        phone_mode: 'messages'
                    },
                    'landing-page': {
                        phone_mode: 'browser'
                    }
                }
            };
        },

        resetDemo() {
            this.shift_status = 'available';
            this.phone_key++;
            this.initializeSteps(this.getStepConfig());
        },

        resetWebExperience() {
            this.shift_status = 'available';
            this.phone_key++;
        },

        handleGoToLandingPage() {
            this.navigateToStep('landing-page');
        },

        handleClaimShift() {
            this.shift_status = 'claimed';
        },

        handlePassShift() {
            this.shift_status = 'passed';
        },

        handleOtherCaregiverClaim() {
            this.shift_status = 'claimed_by_other';
        },

        handleUndoOtherCaregiverClaim() {
            this.shift_status = 'available';
        }
    },

    mounted() {
        document.title = "Healthcare Shift Scheduling Demo | RedChirp";
        this.initializeSteps(this.getStepConfig());
    }
}
</script>

<style lang="scss" scoped></style>
